import React from 'react'
import InfoIcon from '../../images/svg/InfoIcon'
import WarningIcon from '../../images/svg/WarningIcon'
import ErrorIcon from '../../images/svg/ErrorIcon'
import CheckIcon from '../../images/svg/CheckIcon'

interface InlineToastProps {
  message: string
  title: string
  variant: 'success' | 'error' | 'warning' | 'info'
  children?: React.ReactNode
  addBorder?: boolean
}

const getVariantClasses = (variant: InlineToastProps['variant']) => {
  const baseClasses = 'shadow-slate-200 dark:shadow-slate-900'
  // Default for info variant or no variant specified
  const defaults = `${baseClasses} bg-indigo-50 dark:bg-indigo-800 text-indigo-600 dark:text-indigo-200 border-indigo-600 dark:border-indigo-500/60`

  switch (variant) {
    case 'success':
      return `${baseClasses} bg-green-50 dark:bg-green-800 text-green-600 dark:text-green-200 border-green-600 dark:border-green-500/60`
    case 'error':
      return `${baseClasses} bg-red-50 dark:bg-red-800 text-red-600 dark:text-red-200 border-red-600 dark:border-red-500/60`
    case 'warning':
      return `${baseClasses} bg-amber-50 dark:bg-amber-800 text-amber-600 dark:text-amber-200 border-amber-600 dark:border-amber-500/60`
    case 'info':
      return defaults
    default:
      return defaults
  }
}

const getVariantIcons = (variant: InlineToastProps['variant']) => {
  switch (variant) {
    case 'success':
      return (
        <CheckIcon
          className="h-6 w-6 shrink-0 rounded-full bg-green-200 text-green-600 dark:bg-green-950 dark:text-green-300"
          aria-hidden
        />
      )
    case 'error':
      return (
        <ErrorIcon
          className="h-6 w-6 shrink-0 text-red-600 dark:text-red-300"
          aria-hidden
        />
      )
    case 'warning':
      return (
        <WarningIcon
          className="h-6 w-6 shrink-0 text-amber-600 dark:text-amber-300"
          aria-hidden
        />
      )
    case 'info':
      return (
        <InfoIcon
          className="h-6 w-6 shrink-0 text-indigo-600 dark:text-indigo-300"
          aria-hidden
        />
      )
    default:
      return (
        <InfoIcon
          className="h-6 w-6 shrink-0 text-gray-600 dark:text-slate-300"
          aria-hidden
        />
      )
  }
}

export default function InlineToast({
  message,
  title,
  variant,
  children,
  addBorder = false,
}: InlineToastProps & {children?: React.ReactNode}) {
  return (
    <div
      className={[
        `w-full flex flex-col items-center gap-2`,
        'shadow-md p-3 rounded-md text-sm',
        addBorder ? 'border' : '',
        `${getVariantClasses(variant)}`,
      ].join(' ')}
      role="alert"
    >
      <div className="w-full items-center flex gap-2">
        {getVariantIcons(variant)}
        <div className="flex flex-col pl-1">
          <p className="text-base font-bold">{title}</p>
        </div>
      </div>
      <p className="text-sm w-full">{message}</p>
      {children}
    </div>
  )
}
